export const PARTICIPATION_LAYER_KINDS = [
    'stage_primary',
    'guest_temp',
    'audience_gallery',
] as const;

export type ParticipationLayerKind = typeof PARTICIPATION_LAYER_KINDS[number];

export const PRESENCE_MODES = [
    'webrtc_bidirectional',
    'audio_call_in',
    'embedded_stream',
    'companion_app_only',
] as const;

export type PresenceMode = typeof PRESENCE_MODES[number];

export interface ParticipationCapabilities {
    canSpeakLive: boolean;
    canDialInAudio: boolean;
    canVote: boolean;
    canRespondToPolls: boolean;
    canChat: boolean;
    canRaiseHand: boolean;
    canRequestPromotion: boolean;
    canModeratePromotions: boolean;
    canModerateContributions: boolean;
    canMuteParticipants: boolean;
    canCloseCallInWindow: boolean;
    canManagePolls: boolean;
    canBlockAudienceSessions: boolean;
    receivesTranscript: boolean;
    receivesRealtimeState: boolean;
}

export interface ParticipationLayerDefinition {
    kind: ParticipationLayerKind;
    label: string;
    presenceMode: PresenceMode;
    capabilities: ParticipationCapabilities;
    maxParticipants?: number;
}

export interface ParticipationLayerOverride {
    label?: string;
    presenceMode?: PresenceMode;
    capabilities?: Partial<ParticipationCapabilities>;
    maxParticipants?: number;
}

export type ThreeTierParticipationLayerOverrides = Partial<
    Record<ParticipationLayerKind, ParticipationLayerOverride>
>;

const THREE_TIER_PARTICIPATION_BASE: Record<
    ParticipationLayerKind,
    ParticipationLayerDefinition
> = {
    stage_primary: {
        kind: 'stage_primary',
        label: 'Primary Stage',
        presenceMode: 'webrtc_bidirectional',
        capabilities: {
            canSpeakLive: true,
            canDialInAudio: false,
            canVote: false,
            canRespondToPolls: true,
            canChat: true,
            canRaiseHand: false,
            canRequestPromotion: false,
            canModeratePromotions: true,
            canModerateContributions: true,
            canMuteParticipants: true,
            canCloseCallInWindow: true,
            canManagePolls: true,
            canBlockAudienceSessions: true,
            receivesTranscript: true,
            receivesRealtimeState: true,
        },
    },
    guest_temp: {
        kind: 'guest_temp',
        label: 'Guest Speaker',
        presenceMode: 'audio_call_in',
        capabilities: {
            canSpeakLive: true,
            canDialInAudio: true,
            canVote: false,
            canRespondToPolls: true,
            canChat: false,
            canRaiseHand: false,
            canRequestPromotion: false,
            canModeratePromotions: false,
            canModerateContributions: false,
            canMuteParticipants: false,
            canCloseCallInWindow: false,
            canManagePolls: false,
            canBlockAudienceSessions: false,
            receivesTranscript: true,
            receivesRealtimeState: true,
        },
        maxParticipants: 1,
    },
    audience_gallery: {
        kind: 'audience_gallery',
        label: 'Audience Gallery',
        presenceMode: 'embedded_stream',
        capabilities: {
            canSpeakLive: false,
            canDialInAudio: true,
            canVote: true,
            canRespondToPolls: true,
            canChat: true,
            canRaiseHand: true,
            canRequestPromotion: true,
            canModeratePromotions: false,
            canModerateContributions: false,
            canMuteParticipants: false,
            canCloseCallInWindow: false,
            canManagePolls: false,
            canBlockAudienceSessions: false,
            receivesTranscript: true,
            receivesRealtimeState: true,
        },
    },
};

const buildParticipationLayerDefinition = (
    definition: ParticipationLayerDefinition,
    override?: ParticipationLayerOverride,
): ParticipationLayerDefinition => ({
    kind: definition.kind,
    label: override?.label ?? definition.label,
    presenceMode: override?.presenceMode ?? definition.presenceMode,
    capabilities: {
        ...definition.capabilities,
        ...(override?.capabilities ?? {}),
    },
    maxParticipants: override?.maxParticipants ?? definition.maxParticipants,
});

export const buildThreeTierParticipationLayers = (
    overrides?: ThreeTierParticipationLayerOverrides,
): ParticipationLayerDefinition[] => PARTICIPATION_LAYER_KINDS.map((kind) =>
    buildParticipationLayerDefinition(
        THREE_TIER_PARTICIPATION_BASE[kind],
        overrides?.[kind],
    ));

export const THREE_TIER_PARTICIPATION: ParticipationLayerDefinition[] =
    buildThreeTierParticipationLayers();

export const getThreeTierParticipationLayerDefinition = (
    kind: ParticipationLayerKind,
): ParticipationLayerDefinition => {
    const layerDefinition = THREE_TIER_PARTICIPATION.find((layer) => layer.kind === kind);

    if (!layerDefinition) {
        throw new Error(`Shared three-tier participation layer is not configured: ${kind}`);
    }

    return layerDefinition;
};

export const getThreeTierParticipationCapabilities = (
    kind: ParticipationLayerKind,
): ParticipationCapabilities => getThreeTierParticipationLayerDefinition(kind).capabilities;
